import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { API_BASE_URL } from '../../constants';
import AddToCartButton from '../Cart/AddToCartButton';
import './ProjectCard.css';

function ProjectCard({ project, currentUser, onProjectDeleted }) {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = currentUser && currentUser.uid === project.ownerId;
  const createdOn = project.createdAt?.toDate?.()?.toLocaleDateString();
  const galleryUrls = project.imageUrls?.length
    ? project.imageUrls
    : project.imageUrl
    ? [project.imageUrl]
    : [];
  const galleryFileNames = project.imageFileNames?.length
    ? project.imageFileNames
    : project.imageFileName
    ? [project.imageFileName]
    : [];
  const primaryImageUrl = galleryUrls[0] || null;
  
  const handleCardClick = () => {
    navigate(`/marketplace?id=${project.projectId}`);
  };

  const handleCardKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    setDeleting(true);
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'marketplace-projects', project.id));
      
      // TODO: Call Python script to delete from GCS
      // For now, just remove from UI
      onProjectDeleted();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const downloadFile = async (fileType) => {
    if (!isOwner) {
      alert('Only the project owner can download files');
      return;
    }

    try {
      // Determine which file to download
      let fileName;
      if (fileType === 'STEP') {
        fileName = project.stepFileName;
      } else if (fileType === 'XML') {
        fileName = project.xmlFileName;
      } else if (fileType === 'Image' && galleryUrls.length) {
        const [firstUrl] = galleryUrls;
        const inferredName = firstUrl ? firstUrl.split('?')[0].split('/').pop() : null;
        fileName = galleryFileNames[0] || inferredName;
      }

      if (!fileName) {
        alert('File not found');
        return;
      }

      // Call backend to download file
      const url = `${API_BASE_URL}/api/download-file?folderName=${encodeURIComponent(project.folderName)}&fileName=${encodeURIComponent(fileName)}`;
      
      // Fetch the file as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      
      // Create a download link with the blob
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName; // This will be the filename for download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please ensure backend server is running.');
    }
  };

  const hasImage = Boolean(primaryImageUrl);
  const placeholderColor = project.imageColor || '#312e81';

  return (
    <div
      className="project-card"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div
        className={`project-card__image ${hasImage ? 'project-card__image--has-media' : 'project-card__image--placeholder'}`}
        style={
          hasImage
            ? undefined
            : {
                '--project-card-placeholder-color': placeholderColor,
              }
        }
      >
        {primaryImageUrl ? (
          <img 
            src={primaryImageUrl} 
            alt={project.name}
            onError={(e) => {
              console.error('Failed to load image:', primaryImageUrl);
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="project-card__placeholder">${project.name?.charAt(0).toUpperCase() || 'P'}</div>`;
            }}
            onLoad={() => console.log('Image loaded successfully:', primaryImageUrl)}
          />
        ) : (
          <div className="project-card__placeholder">
            {project.name?.charAt(0).toUpperCase() || 'P'}
          </div>
        )}
      </div>

      <div className="project-card__content">
  <h3 className="project-card__title">{project.name}</h3>
  <p className="project-card__creator">by {project.ownerName || 'Anonymous'}</p>
        
        {project.description && (
          <p className="project-card__description">
            {project.description}
          </p>
        )}

        <div className="project-card__meta">
          <span className="project-card__date">
            Created on: {createdOn || 'Recently'}
          </span>
          <span className="project-card__price">
            {project.price && project.price > 0
              ? `$${Number(project.price).toFixed(2)}`
              : 'Free'}
          </span>
        </div>

        <div
          className="project-card__actions"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <AddToCartButton project={project} />
        </div>
      </div>
    </div>
  );
}

export default ProjectCard;
